import {
  OakFlex,
  OakInlineBanner,
  OakInlineBannerProps,
  OakPrimaryButton,
} from "@oaknational/oak-components";

import { getBannerContent, CYCLE_2_SUBJECTS } from "./getBannerContent";
import { EmailCaptureBanner } from "./EmailCaptureBanner";

import { UnitListingData } from "@/node-lib/curriculum-api-2023/queries/unitListing/unitListing.schema";
import { UnitsSectionData } from "@/pages/pupils/programmes/[programmeSlug]/units";
import useMediaQuery from "@/hooks/useMediaQuery";

type UnitListCurrentPageItems = Omit<
  UnitListingData["units"][number][number],
  "unitStudyOrder"
>[][];

export const getIsUnitExpiring = (
  units: UnitListCurrentPageItems | UnitsSectionData["units"],
) => {
  return units.some((unit) =>
    unit?.some((unitItem) => unitItem.actions?.displayExpiringBanner),
  );
};

export const getDoesSubjectHaveNewUnits = (subjectSlug: string) => {
  return subjectSlug !== "drama" && subjectSlug !== "latin";
};

export const TakedownBanner = ({
  hasNewUnits,
  subjectSlug,
  userType,
  onButtonClick,
  onwardHref,
  isLegacy,
  isExpiring,
  isSingle,
  titleTag = "h2",
}: {
  hasNewUnits: boolean;
  subjectSlug: string;
  userType: "teacher" | "pupil";
  onButtonClick?: () => void;
  onwardHref?: string;
  isLegacy: boolean;
  isExpiring: boolean;
  isSingle?: boolean;
  titleTag?: OakInlineBannerProps["titleTag"];
}) => {
  const isMobile = useMediaQuery("mobile");
  const isCycle2 = CYCLE_2_SUBJECTS.has(subjectSlug);

  if (!isLegacy || (!isExpiring && !isCycle2)) {
    return null;
  }

  const bannerContent = getBannerContent({
    isCycle2,
    isExpiring,
    hasNewUnits,
    user: userType,
    isSingle,
  });
  const shouldHideButton = isCycle2 && !hasNewUnits;

  if (!bannerContent) {
    return null;
  }

  // Capture emails for newsletter if user is a teacher and no new units exist
  const shouldUseEmailCapture =
    userType === "teacher" && !hasNewUnits && isExpiring;

  if (shouldUseEmailCapture) {
    return (
      <EmailCaptureBanner
        header={bannerContent.header}
        body={bannerContent.body}
      />
    );
  }

  const buttonText = `View ${!isMobile && userType === "teacher" ? "and download " : ""}new ${userType === "teacher" ? "resources" : "lessons"}`;

  return (
    <OakInlineBanner
      isOpen={true}
      message={bannerContent.body}
      title={bannerContent.header}
      $width={"100%"}
      type={"warning"}
      variant={"large"}
      titleTag={titleTag}
      cta={
        (onwardHref || onButtonClick) &&
        !shouldHideButton && (
          <OakFlex>
            <OakPrimaryButton
              element="a"
              iconName="arrow-right"
              isTrailingIcon
              onClick={onButtonClick}
              tabIndex={0}
              href={onwardHref}
            >
              {buttonText}
            </OakPrimaryButton>
          </OakFlex>
        )
      }
    />
  );
};
